import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import {
    FAQ_ASK_QUESTION_FORM_NAME,
    FaqAskQuestionFormData,
} from "@/mk2/apps/faq/components/FaqAskQuestionForm";
import { faqIndexUrl } from "@/mk2/apps/faq/urls";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import { AppName } from "@/mk2/containers/Application/Application";
import {
    mkReduxFormMapDispatchToProps,
    mkReduxFormMapStateToProps,
} from "@/mk2/decorators/mkReduxForm";
import { memoizeOne } from "@/mk2/helpers/cache";
import { cachedInitialValues } from "@/mk2/helpers/form";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import FormPage, { FormPageDispatchProps, FormPageStateProps } from "@/mk2/pages/FormPage";
import { BrandingProps, HalfpageProps, RightContentSide } from "@/mk2/pages/Page";
import {
    AppState,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { UserEntity } from "@/mk2/schemas";
import { Store } from "@/mk2/store/configureStore";
import { SEND, SEO_TITLE_FAQ_ASK_QUESTION } from "@/mk/autogenerated/translation";

import FaqAskQuestion from "./FaqAskQuestion";
import { faqAskQuestionFetchTrigger, faqAskQuestionSendTrigger } from "./FaqAskQuestion.actions";
import reducer, { AskQuestionState } from "./FaqAskQuestion.reducers";
import saga from "./FaqAskQuestion.sagas";
import { getFaqAskQuestionState } from "./FaqAskQuestion.selectors";

interface OwnProps {}

interface StateProps extends FormPageStateProps {
    errorMessage: string;
    faqId: string;
    initialValues: FaqAskQuestionFormData;
    isMobile: boolean;
    loadingState: LoadingState;
    prefillText: string;
    question: string;
    requestUser: UserEntity;
    requestUserPermissions: string[];
}

interface DispatchProps extends FormPageDispatchProps {
    onLoad(faqId: string);
    onSave(faqId: string, values: FaqAskQuestionFormData);
}

type RouteProps = RouteComponentProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

interface Query {
    faqId: string;
}

export default class FaqAskQuestionPage extends FormPage<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    private parseQuery = memoizeOne((qs: string): Query => {
        const qp = parse(qs) || {};
        return {
            faqId: (qp.faq as string) || null,
        };
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsedQuery: Query = this.parseQuery(ownProps.location.search);
            const faqId = parsedQuery.faqId;

            const questionState: AskQuestionState = getFaqAskQuestionState(state, faqId);

            const prefillText = questionState.prefillText;
            const question = questionState.question;

            return {
                ...mkReduxFormMapStateToProps(FAQ_ASK_QUESTION_FORM_NAME)(state),
                errorMessage: questionState.errorMessage,
                faqId,
                initialValues: cachedInitialValues(
                    FAQ_ASK_QUESTION_FORM_NAME,
                    question && {
                        email: "",
                        message: prefillText,
                        photos: [],
                        username: "",
                    },
                    faqId,
                ),
                isMobile: getRequestDeviceMobile(state),
                loadingState: questionState.loadingState,
                prefillText,
                question,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(FAQ_ASK_QUESTION_FORM_NAME),
            onLoad: faqAskQuestionFetchTrigger,
            onSave: faqAskQuestionSendTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_FAQ_ASK_QUESTION;
    }

    public render(props: Props): false | JSX.Element {
        return <FaqAskQuestion {...props} />;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public getDefaultBackPath(props: Props): string {
        return url(faqIndexUrl, { articleSlug: "faq" });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return null;
    }

    public getBrandingProps(props: Props): BrandingProps {
        return null;
    }

    public getSubmitButton(props: Props): React.ReactElement {
        return <SubmitButton label={SEND} formName={FAQ_ASK_QUESTION_FORM_NAME} />;
    }

    public registerInStore(store: Store<AppState>) {
        // sync registering
        store.injectReducer("containers.faq.faqAskQuestion", reducer);
        store.injectSaga("containers.faq.faqAskQuestion", saga);
    }
}
